import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NovaServiceService, FileAttachment } from '../../../services/nova-service.service';
import { Observable, Subscription } from 'rxjs';
import { FilterSectionComponent } from '../../../components/filter-section/filter-section.component';
import { TimezoneComponent } from '../../../components/timezone/timezone.component';
import { NovaDateRangeFilterComponent } from '../nova-date-range-filter/nova-date-range-filter.component';

@Component({
  selector: 'app-nova-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FilterSectionComponent,
    TimezoneComponent,
    NovaDateRangeFilterComponent,
  ],
  templateUrl: './nova-input.component.html',
  styleUrl: './nova-input.component.scss'
})
export class NovaInputComponent implements OnInit, OnDestroy {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;

  @Input() messageText: string = '';
  @Input() attachments: FileAttachment[] = [];
  @Output() messageTextChange = new EventEmitter<string>();
  @Output() attachmentsChange = new EventEmitter<FileAttachment[]>();
  @Output() sendMessage = new EventEmitter<void>();
  @Output() keyDown = new EventEmitter<KeyboardEvent>();

  // Polling state
  isCurrentConversationPolling$: Observable<boolean>;
  private pollingSubscription?: Subscription;

  constructor(private novaService: NovaServiceService) {
    this.isCurrentConversationPolling$ = new Observable(observer => {
      // Subscribe to both polling state and current conversation changes
      const pollingSub = this.novaService.pollingConversationIds$.subscribe(() => {
        const isPolling = this.novaService.isCurrentConversationPolling();
        observer.next(isPolling);
      });

      const conversationSub = this.novaService.currentConversation$.subscribe(() => {
        const isPolling = this.novaService.isCurrentConversationPolling();
        observer.next(isPolling);
      });

      return () => {
        pollingSub.unsubscribe();
        conversationSub.unsubscribe();
      };
    });
  }

  ngOnInit() {
    // Initialize polling state
    this.pollingSubscription = this.isCurrentConversationPolling$.subscribe();
    if (this.novaService.isChartHandoffActive()) {
      // Keep the date range and filters staged from the chart selection.
      this.novaService.finishChartHandoff();
      return;
    }
    // Default NOVA date range: none (empty start/end in payload)
    this.novaService.setNovaDateRange('', '');
  }

  ngOnDestroy() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    this.keyDown.emit(event);
  }

  adjustTextareaHeight(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  handleAttachment() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv,.xls,.xlsx'; // Only CSV and Excel files
    fileInput.multiple = false; // Only allow single file selection

    fileInput.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const file = files[0]; // Get the single file
        this.novaService.handleSingleFileUpload(file);
      }
    };
    fileInput.click();
  }

  removeAttachment(attachmentId: string) {
    this.novaService.removeAttachment(attachmentId);
  }

  formatFileSize(bytes: number): string {
    return this.novaService.formatFileSize(bytes);
  }

  onMessageTextChange() {
    this.novaService.setMessageText(this.messageText);
    this.messageTextChange.emit(this.messageText);
  }

  onSendMessage() {
    this.novaService.sendMessage();
    this.sendMessage.emit();
  }
}
